
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { AlertCircle, ArrowLeft, Globe } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { useToast } from '@/hooks/use-toast';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import remarkGfm from 'remark-gfm';

type Product = {
  id: string;
  title: string;
  description: string | null;
  introduction?: string | null;
  image: string | null;
  rating: number;
  tags?: string[];
  group_id: string | null;
  group_name?: string;
  site_url?: string;
  publisher?: string;
  published_date?: string;
  created_at: string;
};

interface CodeProps {
  node?: any;
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/products/${id}`, {
        headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
        timeout: 10000,
      });
      setProduct(res.data);
      await fetchTags();
    } catch (err: any) {
      const msg = err instanceof Error ? err.message : '请求失败';
      setError(msg);
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await axios.get(`/api/products/${id}/tags`, {
        headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
        timeout: 5000,
      });
      const tagsData = Array.isArray(res.data) ? res.data.map((t: any) => t.name) : [];
      setTags(tagsData);
    } catch (err) {
      console.error('Error fetching tags:', err);
      setTags([]);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '未知';
    try {
      return new Date(dateString).toLocaleDateString('zh-CN');
    } catch {
      return '未知';
    }
  };

  const handleBreadcrumbNavigation = (path: string) => {
    navigate(path);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              <div className="lg:col-span-3 space-y-6">
                <div className="h-12 bg-gray-200 rounded w-2/3"></div>
                <div className="h-6 bg-gray-200 rounded w-full"></div>
                <div className="h-96 bg-gray-200 rounded"></div>
              </div>
              <div className="lg:col-span-2 space-y-6">
                <div className="h-64 bg-gray-200 rounded"></div>
                <div className="h-48 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  };

  if (error || !product) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert variant="destructive" className="mb-8">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>错误</AlertTitle>
            <AlertDescription>{error || '产品未找到'}</AlertDescription>
          </Alert>
          <Button variant="outline" onClick={() => navigate('/products')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回产品列表
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* 面包屑导航 */}
          <div className="mb-8">
            <Breadcrumb>
              <BreadcrumbList className="text-sm text-gray-500">
                <BreadcrumbItem>
                  <BreadcrumbLink 
                    onClick={() => handleBreadcrumbNavigation('/')}
                    className="hover:text-gray-700 transition-colors cursor-pointer"
                  >
                    Home
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink 
                    onClick={() => handleBreadcrumbNavigation('/products')}
                    className="hover:text-gray-700 transition-colors cursor-pointer"
                  >
                    Category
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {product.group_name && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbLink 
                        onClick={() => {
                          const searchParams = new URLSearchParams();
                          searchParams.set('group', product.group_id || '');
                          navigate(`/products?${searchParams.toString()}`);
                        }}
                        className="hover:text-gray-700 transition-colors cursor-pointer"
                      >
                        {product.group_name}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  </>
                )}
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-gray-900 font-medium">
                    {product.title}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {/* 主要内容区域 - 两列布局 */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* 左侧内容区域 - 占3列 */}
            <div className="lg:col-span-3">
              {/* 上半部分：标题描述和Visit Website按钮 - 与右侧图片对齐 */}
              <div className="flex flex-col h-80 mb-6">
                {/* 产品标题和描述 */}
                <div className="flex-grow">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
                    {product.title}
                  </h1>
                  {product.description && (
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {product.description}
                    </p>
                  )}
                </div>

                {/* Visit Website 按钮 - 固定在底部与右侧图片底部对齐 */}
                <div className="mt-auto">
                  <Button
                    size="lg"
                    className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-medium shadow-sm transition-colors"
                    onClick={() => {
                      if (product.site_url) {
                        window.open(product.site_url, '_blank');
                      } else {
                        toast({ title: '错误', description: '没有设置网站地址', variant: 'destructive' });
                      }
                    }}
                  >
                    <Globe className="h-5 w-5 mr-2" />
                    Visit Website
                  </Button>
                </div>
              </div>

              {/* Introduction 详细介绍区域 - 与右侧信息卡片顶部精确对齐 */}
              {product.introduction && (
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <div className="prose prose-lg max-w-none text-gray-700">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code({node, inline, className, children, ...props}: CodeProps) {
                          const match = /language-(\w+)/.exec(className || '');
                          return !inline && match ? (
                            <SyntaxHighlighter
                              style={prism}
                              language={match[1]}
                              PreTag="div"
                              {...props}
                            >
                              {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                          ) : (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          );
                        }
                      }}
                    >
                      {product.introduction}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              {/* 返回按钮 */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/products')}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </div>
            </div>

            {/* 右侧信息区域 - 占2列 */}
            <div className="lg:col-span-2 space-y-6">
              {/* 产品预览图 - 固定高度320px（h-80）与左侧容器高度一致 */}
              <div className="bg-gray-100 rounded-xl border border-gray-200 overflow-hidden h-80">
                <img
                  src={product.image || '/placeholder.svg'}
                  alt={product.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = '/placeholder.svg';
                  }}
                />
              </div>

              {/* Information 信息卡片 */}
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Publisher</span>
                    <span className="text-gray-900 font-medium">
                      {product.publisher || '未知'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Website</span>
                    <span className="text-gray-900">
                      {product.site_url ? (
                        <a 
                          href={product.site_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          {new URL(product.site_url).hostname}
                        </a>
                      ) : '未设置'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Published date</span>
                    <span className="text-gray-900 font-medium">
                      {formatDate(product.published_date || product.created_at)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Categories 分类卡片 */}
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {product.group_name ? (
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm font-medium">
                        {product.group_name}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-500 text-sm">
                      未分类
                    </span>
                  )}
                </CardContent>
              </Card>

              {/* Tags 标签卡片 */}
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    Tags
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {tags && tags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag, index) => (
                        <span 
                          key={index} 
                          className="inline-block px-2 py-1 text-blue-600 text-sm"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-500 text-sm">
                      暂无标签
                    </span>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetail;
